import { Subject } from 'rxjs/Subject';
import { AmberApi } from 'app/core/models/api-helpers';
import { ConfirmResult } from './../edit-venue-confirm/edit-venue-confirm.component';
import { EditVenueConfirmComponent } from 'app/solar/common/edit-venue-confirm/edit-venue-confirm.component';
import { SimpleInfoComponent } from 'app/shared/components/modal/simple-info.component';
import { SmartModalService } from 'app/core/services/smart-modal.service';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';

import { RequestService } from 'app/core/services/request.service';
import { ConfigService } from 'app/core/services/config.service';
import { FlexibleDropdownConfig } from 'app/shared/components/flexible-dropdown/flexible-dropdown.component';

@Injectable()
export class AddressService {

    constructor(
        private requestService: RequestService,
        private configService: ConfigService,
        private modalService: SmartModalService
    ) {
    }

    addAddress(data: any): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.address.add.api;
        return this.requestService.post(url, data);
    }

    editClassAddress(classId: string, data: any, editAll: boolean): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.address.patch.api +
            '/' + classId + `?editAll=${editAll}`;
        return this.requestService.post(url, data);
    }

    editExamAddress(examId: string, data: any, editAll: boolean): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.exams.address.patch.api +
            '/' + examId + `?editAll=${editAll}`;
        return this.requestService.post(url, data);
    }

    getAddressDetails(id: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.address.details.api + '/' + id;
        return this.requestService.get(url);
    }

    getAddressUsesCount(classId: string, globalAddressId: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.address.usesInClass.api + '/' + classId;
        return this.requestService.get(url, { addressId: globalAddressId });
    }

    listAddressesDictionary(): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.address.dictionary.api;
        return this.requestService.get(url, {page: 1, limit: 100000});
    }
    addressesSimpleDictionary(): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.address.simpleDictionary.api;
        return this.requestService.get(url);
    }

    listClassAddresses(classId: string): Observable<any> {
        let url = this.configService.get('server').url + this.configService.get('endpoints').solar.classevent.address.list.api + '/' + classId;
        return this.requestService.get(url);
    }

    getClassExamVenueFlexConfig(classId?: string, examId?: string, forceRefresh?: Observable<any>): any {
        let config: FlexibleDropdownConfig = {
            label: 'Exam Venue',
            required: true,
            placeholder: 'Select exam venue...',
            addLabel: 'Add New Venue',
            editLabel: 'Edit Venue',
            tooltip: 'classes.edit.exams.venue',
            forceRefresh: forceRefresh,
            dictionaryFactory: () => this.listVenues(classId),
            addFactory: (data) => this.addVenue(data),
            editFactory: examId && ((data) => this.editVenue(classId, examId, data)),
            detailsFactory: examId && ((id) => this.getAddressDetails(id).extract(AmberApi.Data)),
            questions: [
                { key: 'id', hidden: true },
                { key: 'globalAddress', hidden: true },
                { key: 'name', label: 'Venue Name', required: true, tooltip: 'This is a unique identifier of your venue' },
                { key: 'room', label: 'Room Name' },
                { key: 'address', label: 'Address', required: true },
                { key: 'city', label: 'City', required: true },
                { key: 'state', label: 'State' },
                { key: 'province', label: 'Provice' },
                { key: 'country', label: 'Country', required: true },
                { key: 'postalCode', label: 'Postal Code' }
            ]
        };

        return config;
    }

    getClassVenueFlexConfig(classId: string, forceRefresh?: Observable<any>) {
        let config: FlexibleDropdownConfig = {
            label: 'Class Venue',
            required: true,
            placeholder: 'Select class venue...',
            addLabel: 'Add New Venue',
            editLabel: 'Edit Venue',
            tooltip: 'classes.add.location',
            forceRefresh: forceRefresh,
            dictionaryFactory: () => this.listVenues(classId),
            addFactory: (data) => this.addVenue(data),
            editFactory: classId && ((data) => this.editVenue(classId, null, data)),
            detailsFactory: classId && ((id) => this.getAddressDetails(id).extract(AmberApi.Data)),
            questions: [
                { key: 'id', hidden: true },
                { key: 'globalAddress', hidden: true },
                { key: 'name', label: 'Venue Name', required: true, tooltip: 'This is a unique identifier of your venue' },
                { key: 'room', label: 'Room Name' },
                { key: 'address', label: 'Address', required: true },
                { key: 'city', label: 'City', required: true },
                { key: 'state', label: 'State' },
                { key: 'province', label: 'Provice' },
                { key: 'country', label: 'Country', required: true },
                { key: 'postalCode', label: 'Postal Code' }
            ]
        };

        return config;
    }

    private listVenues(classId: string): Observable<any[]> {
        let addressesFetch = classId ? this.listClassAddresses(classId) : this.listAddressesDictionary();
        return addressesFetch
            .extract(AmberApi.Data)
            .map(addresses => addresses.map(a => ({ key: a.id, value: this.getDisplayValue(a) })));
    }

    private addVenue(data) {
        return this.addAddress(data)
            .extract(AmberApi.Data)
            .map(address => ({ key: address.id, value: address.name }))
    }

    private editVenue(classId: string, examId: string, formData: any): Observable<any> {
        return this.getAddressUsesCount(classId, formData.id)
            .extract(AmberApi.Data)
            .flatMap(usesInClass => usesInClass > 1 ?
                this.modalService.open(EditVenueConfirmComponent, {}, 'Edit Venue').map(r => r.result) :
                Observable.of(ConfirmResult.This)
            )
            .map(result => result == ConfirmResult.All)
            .flatMap(editAll => examId ?
                this.editExamAddress(examId, formData, editAll) :
                this.editClassAddress(classId, formData, editAll)
            )
            .extract(AmberApi.Data)
            .map(address => ({ key: address.id, value: this.getDisplayValue(address) }));
    }

    private getDisplayValue(address) {
        let value = address.name;
        if (address.transactional) {
            value += ' (Modified for this class)';
        }

        return value;
    }
}




// WEBPACK FOOTER //
// ./src/app/solar/common/services/address.service.ts